import { Box, Card, Step, StepLabel, Stepper, Typography } from "@mui/material";
import { FC, ReactNode } from "react";

/**
 * Props for the StepsLayoutContainerControlled component
 * @interface StepsLayoutContainerControlledProps
 */
type StepsLayoutContainerControlledProps = {
  /** Array of step labels to be displayed in the stepper */
  steps: string[];
  /** Array of React components to be rendered for each step */
  StepComponentList: {
    label: string;
    component: ReactNode;
  }[];
  /** Current active step index */
  activeStep: string;
  /** Optional flag to indicate if all steps are completed */
  isCompleted?: boolean;

  /** Optional render component for the header */
  renderHeader?: ReactNode;

  /** Current active label */
  activeLabel: number;

  /** Navigate to a high-level stepper phase by index (completed steps only). */
  onStepClick?: (stepIndex: number) => void;
};

/**
 * A controlled stepper layout component that manages multi-step forms with a responsive design.
 * Features include:
 * - Vertical stepper for desktop view
 * - Horizontal stepper for mobile view
 * - Optional timer functionality
 * - Navigation controls (back/next/submit)
 * - Responsive layout adjustments
 *
 * @component
 * @param {StepsLayoutContainerControlledProps} props - Component props
 * @returns {JSX.Element} Rendered component
 */

export const StepsLayoutContainerControlled: FC<
  StepsLayoutContainerControlledProps
> = ({
  steps,
  StepComponentList,
  activeStep,
  activeLabel,
  renderHeader,
  onStepClick,
}) => {
  const renderStepLabel = (label: string, index: number) => {
    const isClickable = Boolean(onStepClick) && index <= activeLabel;
    return (
      <Step
        key={label}
        completed={index < activeLabel}
        sx={{
          cursor: isClickable ? "pointer" : "default",
          "& .MuiStepLabel-root": {
            cursor: isClickable ? "pointer" : "default",
          },
        }}
        onClick={() => {
          if (isClickable) {
            onStepClick?.(index);
          }
        }}
      >
        <StepLabel>
          <Typography
            variant="body1"
            color={index === activeLabel ? "primary" : "textSecondary"}
            sx={{
              textDecoration: isClickable ? "underline" : "none",
              textUnderlineOffset: 2,
              "&:hover": isClickable
                ? { color: "primary.main" }
                : undefined,
            }}
          >
            {label}
          </Typography>
        </StepLabel>
      </Step>
    );
  };

  return (
    <Card sx={{ overflow: "visible" }}>
      <Box
        sx={{
          minHeight: { xs: "calc(100dvh - 78px)", sm: "calc(100dvh - 130px)" },
          display: "flex",
          flexDirection: { sm: "row", xs: "column" },
          position: "relative",
          overflow: "visible",
        }}
      >
        <Box
          width="20%"
          bgcolor="#f5f5f5"
          p={2}
          sx={{ display: { sm: "block", xs: "none" } }}
        >
          <Stepper activeStep={activeLabel} orientation="vertical">
            {steps.map((label, index) => renderStepLabel(label, index))}
          </Stepper>
        </Box>

        <Box
          p={2}
          sx={{
            display: { sm: "none", xs: "block" },
            position: "sticky",
            top: 0,
            backgroundColor: "white",
            zIndex: 1,
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Stepper
            activeStep={activeLabel}
            orientation="horizontal"
            alternativeLabel
          >
            {steps.map((label, index) => (
              <Step
                key={label}
                completed={index < activeLabel}
                sx={{
                  cursor:
                    onStepClick && index <= activeLabel ? "pointer" : "default",
                }}
                onClick={() => {
                  if (onStepClick && index <= activeLabel) {
                    onStepClick(index);
                  }
                }}
              >
                <StepLabel
                  slotProps={{
                    label: {
                      sx: { marginTop: "8px !important" },
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    color={
                      index === activeLabel ? "primary" : "textSecondary"
                    }
                    sx={{
                      textDecoration:
                        onStepClick && index <= activeLabel
                          ? "underline"
                          : "none",
                      textUnderlineOffset: 2,
                    }}
                  >
                    {label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Box
          flex={1}
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "visible",
          }}
        >
          {renderHeader}
          <Box flex={1} sx={{ overflow: "visible" }}>
            {StepComponentList.map(
              ({ label, component: Component }) =>
                activeStep === label && (
                  <Box key={`step_${label}`}>{Component}</Box>
                ),
            )}
          </Box>
        </Box>
      </Box>
    </Card>
  );
};
